import { useState, useMemo, useEffect } from 'react';
import { type Application } from '@/utils/dataStore';
import { apiGetApplications, apiUpdateApplication, apiBatchApproveApplication, apiBatchRejectApplication } from '@/services/applications.api';
import { downloadCsv } from '@/utils/exportCsv';
import { useRBAC } from '@/hooks/useRBAC';
import NoPermission from '@/components/base/NoPermission';

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-600',
};
const STATUS_LABEL: Record<string, string> = { pending: '待审核', approved: '已通过', rejected: '已拒绝' };
const TYPE_LABEL: Record<string, string> = { university: '高校', research: '科研机构', enterprise: '企业', nonprofit: '非营利组织' };
const REGION_LABEL: Record<string, string> = { asia: '亚洲', europe: '欧洲', americas: '美洲', africa: '非洲/中东', oceania: '大洋洲' };

function ExportDropdown({ onExportAll, onExportFiltered, filteredCount }: {
  onExportAll: () => void;
  onExportFiltered: () => void;
  filteredCount: number;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-lg text-xs font-medium text-[#374151] hover:bg-[#F9FAFB] cursor-pointer whitespace-nowrap transition-colors"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-download-2-line text-sm" />
        </div>
        导出 CSV
        <div className="w-3 h-3 flex items-center justify-center">
          {open ? <i className="ri-arrow-up-s-line text-xs" /> : <i className="ri-arrow-down-s-line text-xs" />}
        </div>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-[#E5E7EB] rounded-lg shadow-sm z-20 overflow-hidden">
          <button
            onClick={() => { onExportAll(); setOpen(false); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-[#374151] hover:bg-[#F9FAFB] cursor-pointer transition-colors text-left"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-file-list-3-line text-[#6B7280]" />
            </div>
            导出全部数据
          </button>
          <button
            onClick={() => { onExportFiltered(); setOpen(false); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-[#374151] hover:bg-[#F9FAFB] cursor-pointer transition-colors text-left border-t border-[#F3F4F6]"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-filter-3-line text-[#6B7280]" />
            </div>
            导出筛选结果（{filteredCount}）
          </button>
        </div>
      )}
    </div>
  );
}

export default function ApplicationsPage() {
  const { can } = useRBAC();
  const canView = can('applications:view');
  const canApprove = can('applications:approve');

  const [apps, setApps] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Application | null>(null);
  // Batch
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState('');

  // Load applications from API
  useEffect(() => {
    const loadApplications = async () => {
      setLoading(true);
      try {
        const data = await apiGetApplications();
        setApps(data);
      } catch (error) {
        console.error('加载申请数据失败:', error);
        showToast('加载数据失败');
      } finally {
        setLoading(false);
      }
    };
    loadApplications();
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const filtered = useMemo(() => {
    return apps.filter((a) => {
      if (filterStatus !== 'all' && a.status !== filterStatus) return false;
      if (filterType !== 'all' && a.type !== filterType) return false;
      if (search && !a.companyName.includes(search) && !a.contactName.includes(search) && !a.email.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    }).sort((a, b) => new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime());
  }, [apps, filterStatus, filterType, search]);

  // Batch helpers
  const allFilteredSelected = filtered.length > 0 && filtered.every((a) => selectedIds.has(a.id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((a) => a.id)));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBatchStatus = async (status: Application['status']) => {
    try {
      if (status === 'approved') {
        await apiBatchApproveApplication(Array.from(selectedIds));
      } else if (status === 'rejected') {
        await apiBatchRejectApplication(Array.from(selectedIds));
      }
      const updated = apps.map((a) => selectedIds.has(a.id) ? { ...a, status } : a);
      setApps(updated);
      const labelMap: Record<string, string> = { approved: '通过', rejected: '拒绝', pending: '待审核' };
      showToast(`已将 ${selectedIds.size} 条申请设为「${labelMap[status]}」`);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('批量更新申请状态失败:', error);
      showToast('更新失败，请重试');
    }
  };

  const updateStatus = async (id: string, status: Application['status']) => {
    try {
      await apiUpdateApplication(id, { status });
      const updated = apps.map((a) => a.id === id ? { ...a, status } : a);
      setApps(updated);
      if (selected?.id === id) setSelected({ ...selected, status });
      showToast(`已将申请状态更新为「${status === 'approved' ? '通过' : status === 'rejected' ? '拒绝' : '待审核'}」`);
    } catch (error) {
      console.error('更新申请状态失败:', error);
      showToast('更新失败，请重试');
    }
  };

  const pendingCount = apps.filter((a) => a.status === 'pending').length;

  const APP_CSV_HEADERS = [
    { key: 'companyName', label: '机构名称' },
    { key: 'contactName', label: '联系人' },
    { key: 'email', label: '邮箱' },
    { key: 'phone', label: '电话' },
    { key: 'type', label: '机构类型' },
    { key: 'region', label: '地区' },
    { key: 'description', label: '申请说明' },
    { key: 'appliedAt', label: '申请时间' },
    { key: 'status', label: '状态' },
  ];

  const handleExportAll = () => {
    const rows = apps.map((a) => ({
      ...a,
      type: TYPE_LABEL[a.type] ?? a.type,
      region: REGION_LABEL[a.region] ?? a.region,
      status: STATUS_LABEL[a.status] ?? a.status,
    })) as unknown as Record<string, unknown>[];
    downloadCsv(rows, APP_CSV_HEADERS, `合作申请_全部_${new Date().toISOString().slice(0, 10)}`);
  };

  const handleExportFiltered = () => {
    const rows = filtered.map((a) => ({
      ...a,
      type: TYPE_LABEL[a.type] ?? a.type,
      region: REGION_LABEL[a.region] ?? a.region,
      status: STATUS_LABEL[a.status] ?? a.status,
    })) as unknown as Record<string, unknown>[];
    downloadCsv(rows, APP_CSV_HEADERS, `合作申请_筛选结果_${new Date().toISOString().slice(0, 10)}`);
  };

  if (!canView) return <NoPermission resource="合作申请" />;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {toast && (
        <div className="fixed top-5 right-5 z-50 px-4 py-2.5 bg-[#18181B] text-white text-sm rounded-xl shadow-lg">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#18181B]">合作申请管理</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">共 {apps.length} 条申请，{pendingCount} 条待审核</p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg border border-amber-100">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-amber-600 text-sm" />
              </div>
              <span className="text-amber-700 text-xs font-medium">{pendingCount} 待处理</span>
            </div>
          )}
          <ExportDropdown onExportAll={handleExportAll} onExportFiltered={handleExportFiltered} filteredCount={filtered.length} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-4 h-full">
            <i className="ri-search-line text-[#9CA3AF] text-sm" />
          </div>
          <input
            type="text"
            placeholder="搜索机构/联系人..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] bg-white w-52"
          />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg bg-white focus:outline-none cursor-pointer">
          <option value="all">全部状态</option>
          <option value="pending">待审核</option>
          <option value="approved">已通过</option>
          <option value="rejected">已拒绝</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg bg-white focus:outline-none cursor-pointer">
          <option value="all">全部类型</option>
          <option value="university">高校</option>
          <option value="research">科研机构</option>
          <option value="enterprise">企业</option>
          <option value="nonprofit">非营利</option>
        </select>
        <span className="flex items-center text-xs text-[#9CA3AF] ml-auto">共 {filtered.length} 条结果</span>
      </div>

      {/* Batch Action Bar */}
      {someSelected && (
        <div className="flex items-center gap-3 px-4 py-2.5 mb-3 bg-[#18181B] rounded-xl">
          <span className="text-white text-xs font-medium whitespace-nowrap">已选 {selectedIds.size} 条</span>
          <div className="flex-1" />
          {canApprove && <>
            <button onClick={() => handleBatchStatus('approved')} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-checkbox-circle-line" /></div>批量通过
            </button>
            <button onClick={() => handleBatchStatus('rejected')} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-close-circle-line" /></div>批量拒绝
            </button>
            <button onClick={() => handleBatchStatus('pending')} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#374151] hover:bg-[#4B5563] text-white text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-refresh-line" /></div>撤回为待审
            </button>
          </>}
          <button onClick={() => setSelectedIds(new Set())} className="w-6 h-6 flex items-center justify-center text-white/50 hover:text-white cursor-pointer transition-colors">
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#F3F4F6] bg-[#FAFAFA]">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    className="w-3.5 h-3.5 rounded border-[#D1D5DB] cursor-pointer accent-[#18181B]"
                  />
                </th>
                {['机构名称', '联系人', '类型', '地区', '申请时间', '状态', '操作'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-medium text-[#9CA3AF] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-[#9CA3AF] text-sm">暂无数据</td></tr>
              ) : filtered.map((a) => {
                const isSelected = selectedIds.has(a.id);
                return (
                  <tr key={a.id} className={`border-b border-[#F9FAFB] transition-colors ${isSelected ? 'bg-amber-50/60' : 'hover:bg-[#FAFAFA]'}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectOne(a.id)}
                        className="w-3.5 h-3.5 rounded border-[#D1D5DB] cursor-pointer accent-[#18181B]"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelected(a)} className="font-medium text-[#18181B] hover:text-[#F59E0B] text-xs cursor-pointer transition-colors text-left">
                        {a.companyName}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs whitespace-nowrap">{a.contactName}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280] font-medium">{TYPE_LABEL[a.type] || a.type}</span>
                    </td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs">{REGION_LABEL[a.region] || a.region}</td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs whitespace-nowrap">{new Date(a.appliedAt).toLocaleDateString('zh-CN')}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[a.status]}`}>{STATUS_LABEL[a.status]}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelected(a)} className="px-2 py-1 text-[10px] font-medium text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded cursor-pointer whitespace-nowrap transition-colors">详情</button>
                        {canApprove && a.status !== 'approved' && (
                          <button onClick={() => updateStatus(a.id, 'approved')} className="px-2 py-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded cursor-pointer whitespace-nowrap transition-colors">通过</button>
                        )}
                        {canApprove && a.status !== 'rejected' && (
                          <button onClick={() => updateStatus(a.id, 'rejected')} className="px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded cursor-pointer whitespace-nowrap transition-colors">拒绝</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-[#18181B]">申请详情</h3>
              <button onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#6B7280] cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-3 mb-5">
              {[
                ['机构名称', selected.companyName],
                ['联系人', selected.contactName],
                ['邮箱', selected.email],
                ['电话', selected.phone],
                ['机构类型', TYPE_LABEL[selected.type] || selected.type],
                ['地区', REGION_LABEL[selected.region] || selected.region],
                ['申请时间', new Date(selected.appliedAt).toLocaleString('zh-CN')],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-3">
                  <span className="text-xs text-[#9CA3AF] w-20 shrink-0 pt-0.5">{k}</span>
                  <span className="text-sm text-[#18181B]">{v}</span>
                </div>
              ))}
              <div className="flex gap-3">
                <span className="text-xs text-[#9CA3AF] w-20 shrink-0 pt-0.5">申请说明</span>
                <p className="text-sm text-[#374151] leading-relaxed flex-1">{selected.description}</p>
              </div>
              <div className="flex gap-3">
                <span className="text-xs text-[#9CA3AF] w-20 shrink-0 pt-1.5">当前状态</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLOR[selected.status]}`}>{STATUS_LABEL[selected.status]}</span>
              </div>
            </div>
            {canApprove && (
              <div className="flex gap-2">
                {selected.status !== 'approved' && (
                  <button onClick={() => updateStatus(selected.id, 'approved')} className="flex-1 py-2.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg cursor-pointer whitespace-nowrap transition-colors">通过申请</button>
                )}
                {selected.status !== 'rejected' && (
                  <button onClick={() => updateStatus(selected.id, 'rejected')} className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg cursor-pointer whitespace-nowrap transition-colors">拒绝申请</button>
                )}
                {selected.status !== 'pending' && (
                  <button onClick={() => updateStatus(selected.id, 'pending')} className="flex-1 py-2.5 text-sm font-medium text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg cursor-pointer whitespace-nowrap transition-colors">撤回审批</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
